/**
 * Which Fun style a full moment plays.
 *
 * Pure, with the randomness passed in so the rules can be tested exactly.
 *
 *   - Each fixed event has its own style:
 *       a named version  → the safe ("locked in" is what ⌘S means);
 *       back online      → Lili, waving (relief deserves a face);
 *       recovery         → the stamp (SAVED lands where the error was).
 *   - The one random pick is a new document's first save:
 *       Lili 5 · stamp 2 · safe 1, never the same style twice in a row.
 *
 * Fun only styles FULL moments; ticks look the same in Pro and Fun.
 * Design: lilia-docs/design-handoffs/2026-09-27-saved-moment/ §4.
 */
import type { FullEvent } from "./momentRules";

/** `lili-wave` is Lili's one extra pose; for "twice in a row" it is Lili. */
export type FunStyle = "lili" | "lili-wave" | "stamp" | "safe";
type Family = "lili" | "stamp" | "safe";

export const FUN_WEIGHTS: Readonly<Record<Family, number>> = { lili: 5, stamp: 2, safe: 1 };

const FIXED: Partial<Record<FullEvent, FunStyle>> = {
  version: "safe",
  "back-online": "lili-wave",
  recovered: "stamp",
};

export function funFamily(style: FunStyle): Family {
  return style === "lili-wave" ? "lili" : style;
}

/**
 * @param random  a number in [0, 1), e.g. Math.random.
 */
export function pickFunStyle(
  event: FullEvent,
  previous: FunStyle | null,
  random: () => number,
): FunStyle {
  const fixed = FIXED[event];
  if (fixed) return fixed;

  const exclude = previous ? funFamily(previous) : null;
  const pool = (Object.keys(FUN_WEIGHTS) as Family[]).filter((f) => f !== exclude);
  const total = pool.reduce((sum, f) => sum + FUN_WEIGHTS[f], 0);
  let roll = random() * total;
  for (const family of pool) {
    roll -= FUN_WEIGHTS[family];
    if (roll < 0) return family;
  }
  return pool[pool.length - 1];
}
